import PreventiveMaintenanceGrid from "./PreventiveAppointmentGrid/PreventiveAppointmentGrid";
import { Button } from "primereact/button";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useState } from "react";
import useRefreshData from "src/hooks/useRefreshData";
import {
  getPreventiveAppointment,
  resetFilterData,
  selectPreventiveAppointmentsForExport,
  setFilterDataField,
  setGridCurrentPage,
} from "src/redux/slices/preventiveAppointmentSlice";
import PreventiveAppointmentFilter from "../PreventiveAppointmentFilter";
import { ReactComponent as ExportIcon } from "../../../../assets/icons/export.svg";
import { isCivilDefenseUser, isRtl } from "src/utils/helpers";
import { DownloadViewPrefixes } from "src/resources/enum";
import _ from "lodash";
import {
  CDPreventiveAppointmentsOrderedColumns,
  PreventiveAppointmentsOrderedColumns,
} from "src/resources/constant";
import HorizontalDivider from "src/components/HorizontalDivider/HorizontalDivider";
import "./PreventiveAppointmentList.scss";
import { exportFormattedData } from "src/utils/common";
import { getAppointmentsStatuses } from "src/redux/slices/lookupSlice";

import ShowHistoricalData from "src/components/ShowHistoricalData/ShowHistoricalData";
import { setChangeHistoricalDataSource } from "src/redux/slices/sharedSlice";
const PreventiveAppointmentList = ({ isBreached = false }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation(["common", "registration"]);

  const [shouldExport, setShouldExport] = useState(false);

  const breadCrumbsPaths = useSelector(
    (state: any) => state.breadCrumb.breadCrumbItems
  );
  const language = useSelector((state: any) => state.lookup.language);

  const preventiveAppointmentDataForExport = useSelector(
    selectPreventiveAppointmentsForExport
  );
  const { showHistoricalData } = useSelector(
    (state: any) => state.preventiveAppointments?.filterData
  );
  const { showHistoricalDataChangeSource } = useSelector(
    (state: any) => state.shared
  );
  useRefreshData(getPreventiveAppointment);

  useEffect(() => {
    // reset filter data and search text
    dispatch(resetFilterData());
    dispatch(getAppointmentsStatuses() as any);
  }, [dispatch]);

  const exportFile = useCallback(() => {
    if (preventiveAppointmentDataForExport.length > 0) {
      let mappedData = getMappedData(
        preventiveAppointmentDataForExport,
        language
      );
      const columns = isCivilDefenseUser()
        ? _.cloneDeep(CDPreventiveAppointmentsOrderedColumns)
        : _.cloneDeep(PreventiveAppointmentsOrderedColumns);

      const fileNamePrefix = isBreached
        ? DownloadViewPrefixes.BreachedPreventiveAppointments
        : DownloadViewPrefixes.PreventiveAppointments;
      if (!isBreached) {
        columns.push("completionDate");
      }
      exportFormattedData(mappedData, columns, fileNamePrefix, t);
    }
  }, [preventiveAppointmentDataForExport, language, t]);

  useEffect(() => {
    if (shouldExport) {
      exportFile();
      setShouldExport(false);
    }
  }, [exportFile, shouldExport]);

  const exportExcelHandler = (): void => {
    dispatch(getPreventiveAppointment(true) as any).then(() => {
      setShouldExport(true);
    });
  };

  const handleFormFieldChange = (e: any) => {
    const name = e.target.name;
    const value = e.target.checked;
    dispatch(setFilterDataField({ field: name, value }));
  };
  useEffect(() => {
    dispatch(setGridCurrentPage(0));
  }, [dispatch]);

  useEffect(() => {
    if(showHistoricalDataChangeSource === "") return;
    const index = parseInt(showHistoricalDataChangeSource.toString());
    if (index >= 10) //the number that we set in the ShowHistoricalData component
      dispatch(setGridCurrentPage(0));
      dispatch(setChangeHistoricalDataSource(""));
  }, [showHistoricalDataChangeSource]);

  return (
    <div className="py-2">
      <div className="d-flex justify-content-between px-3 align-items-center pb-2">
        {breadCrumbsPaths.length && (
          <h2 className="inner-page-title">
            {t(breadCrumbsPaths[breadCrumbsPaths.length - 1].label)}
          </h2>
        )}
        <div className="inner-page-search  gap-2 ">
          {!isBreached && (
            <ShowHistoricalData
              handleFormFieldChange={handleFormFieldChange}
              showHistoricalData={showHistoricalData}
            />
          )}
          <Button
            className="d-flex gap-2 light-primary-btn m-0"
            label={t("export")}
            icon={<ExportIcon className="m-0" />}
            onClick={exportExcelHandler}
          ></Button>
        </div>
      </div>
      <HorizontalDivider />
      <PreventiveAppointmentFilter />
      <HorizontalDivider />
      <PreventiveMaintenanceGrid />
    </div>
  );
};

export default PreventiveAppointmentList;
function getMappedData(preventiveAppointmentDataForExport: any, language: any) {
  let mappedData = preventiveAppointmentDataForExport;
  if (isRtl(language)) {
    mappedData = mappedData.map((preventiveAppointment: any) => ({
      ...preventiveAppointment,
      appointmentType: preventiveAppointment?.appointmentTypeAr,
      typeOfApproval: preventiveAppointment?.onHoldReasonAr,
      propertyID: preventiveAppointment?.propertyNumber,
      status: preventiveAppointment?.statusAr
        ? preventiveAppointment?.statusAr
        : preventiveAppointment?.statusEn,
    }));
  } else {
    mappedData = mappedData.map((preventiveAppointment: any) => ({
      ...preventiveAppointment,
      appointmentType: preventiveAppointment?.appointmentTypeEn,
      typeOfApproval: preventiveAppointment?.onHoldReasonEn,
      propertyID: preventiveAppointment?.propertyNumber,
      status: preventiveAppointment?.statusEn,
    }));
  }

  return mappedData;
}
